/*
 * Copyright (c) 2023. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DoneAllRoundedIcon from '@mui/icons-material/DoneAllRounded';
import { alpha, Box, Button, IconButton, Stack, Typography } from '@mui/material';
import type { UINotification } from '@poweralpha/common';
import type { MouseEvent } from 'react';
import type { ToastContentProps } from 'react-toastify';
import '../toast.css';
import { trimString } from '../../utils';
import { formatNotificationTime, getNotificationActionLabel } from '../display';
import NotificationGlyph from './NotificationGlyph';
import { renderNotificationContent } from './notificationContent';
import { getMessageQuoteSx, lineClampSx } from './styles';

interface Props {
  closeToast: ToastContentProps['closeToast'];
  content: string;
  notification: UINotification;
  handleClick: (notif: UINotification, redirect?: boolean) => void;
}

const actionSx = {
  minWidth: 0,
  px: 1,
  py: 0.25,
  fontSize: 12,
  whiteSpace: 'nowrap',
  textTransform: 'none',
} as const;

/** Notification toast (top-right, under the menu cluster): open it, mark it read or dismiss it. */
const NotificationToast = ({ content, closeToast, handleClick, notification }: Readonly<Props>) => {
  const { subject, link, message, severity, source } = notification;

  const open = () => {
    handleClick(notification);
    closeToast('notification-click');
  };
  const markRead = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    handleClick(notification, false);
    closeToast('manual-dismiss');
  };

  return (
    <Box
      className="notification-toast-shell"
      onClick={open}
      sx={{ display: 'flex', gap: 1.5, p: '12px 8px 12px 12px', cursor: 'pointer' }}
    >
      <NotificationGlyph source={source} severity={severity} />
      <Stack spacing={0.375} sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Typography noWrap sx={{ flex: 1, fontSize: 13, fontWeight: 600 }}>
            {subject}
          </Typography>
          <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
            {formatNotificationTime(notification.created_at)}
          </Typography>
          <IconButton
            size="small"
            aria-label="Dismiss notification"
            onClick={markRead}
            sx={{ p: 0.25, color: 'text.secondary' }}
          >
            <CloseRoundedIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </Stack>
        <Typography
          sx={[{ fontSize: 12, lineHeight: '17px', color: 'text.secondary' }, lineClampSx(2)]}
        >
          {renderNotificationContent(trimString(content, 300), link)}
        </Typography>
        {message && (
          <Typography noWrap sx={[{ fontSize: 12 }, getMessageQuoteSx(`${severity}.main`)]}>
            {message}
          </Typography>
        )}
        <Stack direction="row" spacing={0.5} sx={{ pt: 0.75 }}>
          <Button
            size="small"
            endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: 14 }} />}
            onClick={(event) => {
              event.stopPropagation();
              open();
            }}
            sx={[
              actionSx,
              (theme) => ({ fontWeight: 600, bgcolor: alpha(theme.palette.primary.main, 0.14) }),
            ]}
          >
            {getNotificationActionLabel(notification) ?? 'Open details'}
          </Button>
          <Button
            size="small"
            color="inherit"
            startIcon={<DoneAllRoundedIcon sx={{ fontSize: 14 }} />}
            onClick={markRead}
            sx={[actionSx, { color: 'text.secondary' }]}
          >
            Mark read
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default NotificationToast;
